import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { signedUrl } from '../lib/upload'
import { useI18n } from '../i18n/useI18n'
import { formatDate } from '../lib/format'
import type { DocKind } from '../lib/types'

interface DocRow {
  id: string
  storage_path: string
  kind: DocKind
  label: string | null
  mime_type: string | null
  byte_size: number | null
  uploaded_at: string
  period: string | null
}

/**
 * Affichage d'une piece justificative.
 *
 * Le seau est prive : il n'existe aucune URL publique. Chaque
 * consultation demande une URL signee valable une heure, ce qui evite
 * qu'un lien copie circule indefiniment.
 *
 * Les PDF ne s'affichent pas de facon fiable dans un cadre sur
 * Android : on propose donc toujours l'ouverture dans un onglet, et le
 * partage par le systeme quand le telephone le permet, ce qui donne
 * WhatsApp.
 */
export function DocumentViewer({
  documentId,
  onClose,
}: {
  documentId: string
  onClose: () => void
}) {
  const { t } = useI18n()
  const [doc, setDoc] = useState<DocRow | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let actif = true
    void (async () => {
      setLoading(true)
      setError(null)

      const { data, error: e } = await supabase
        .from('documents')
        .select('id, storage_path, kind, label, mime_type, byte_size, uploaded_at, period')
        .eq('id', documentId)
        .single()

      if (!actif) return
      if (e) {
        setError(e.message)
        setLoading(false)
        return
      }

      const row = data as DocRow
      setDoc(row)

      const signed = await signedUrl(row.storage_path)
      if (!actif) return
      if (!signed) setError(t('doc_url_failed'))
      setUrl(signed)
      setLoading(false)
    })()
    return () => {
      actif = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId])

  const estImage = (doc?.mime_type ?? '').startsWith('image/')
  const estPdf = (doc?.mime_type ?? '') === 'application/pdf'

  /** Partage par le systeme : donne WhatsApp sur Android. */
  async function share() {
    if (!url || !doc) return
    try {
      const reponse = await fetch(url)
      const blob = await reponse.blob()
      const fichier = new File([blob], nomFichier(doc), {
        type: blob.type || 'application/octet-stream',
      })
      if (navigator.canShare?.({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: doc.label ?? doc.kind })
        return
      }
      // Sans partage de fichier, on partage au moins le lien signe.
      if (navigator.share) await navigator.share({ url })
    } catch {
      // Partage refuse ou annule : rien a signaler.
    }
  }

  function nomFichier(d: DocRow): string {
    const ext = d.storage_path.split('.').pop() ?? 'jpg'
    const base = [d.kind, d.period].filter(Boolean).join('-')
    return `${base}.${ext}`
  }

  return (
    <div className="sheet__backdrop" onClick={onClose}>
      <div
        className="sheet sheet--viewer"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <h2 className="sheet__title">
            {doc ? t(`doc_kind_${doc.kind}` as DocKindKey) : t('loading')}
          </h2>
          <button
            type="button"
            className="sheet__close"
            aria-label={t('cancel')}
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="viewer">
          {loading && <p className="muted">{t('loading')}</p>}
          {error && <p className="alert alert--error">{error}</p>}

          {url && estImage && (
            <img className="viewer__image" src={url} alt={doc?.label ?? ''} />
          )}

          {url && estPdf && (
            <div className="viewer__pdf">
              <p className="muted">{t('doc_pdf_hint')}</p>
            </div>
          )}

          {url && !estImage && !estPdf && (
            <p className="muted">{t('doc_no_preview')}</p>
          )}

          {doc && (
            <dl className="deflist">
              <dt>{t('doc_uploaded_at')}</dt>
              <dd>{formatDate(doc.uploaded_at.slice(0, 10))}</dd>
              {doc.byte_size !== null && (
                <>
                  <dt>{t('doc_size')}</dt>
                  <dd>{Math.max(1, Math.round(doc.byte_size / 1024))} Ko</dd>
                </>
              )}
            </dl>
          )}
        </div>

        <div className="sheet__foot">
          {url && (
            <a
              className="btn btn--ghost"
              href={url}
              target="_blank"
              rel="noreferrer noopener"
            >
              {t('doc_open')}
            </a>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={!url}
            onClick={() => void share()}
          >
            {t('doc_share')}
          </button>
        </div>
      </div>
    </div>
  )
}

type DocKindKey =
  | 'doc_kind_contrat' | 'doc_kind_quittance' | 'doc_kind_recu'
  | 'doc_kind_facture' | 'doc_kind_bordereau' | 'doc_kind_etat_des_lieux'
  | 'doc_kind_piece_identite' | 'doc_kind_photo' | 'doc_kind_autre'

/** Petit bouton « voir la piece », a placer partout ou un document existe. */
export function DocumentLink({
  documentId,
  label,
}: {
  documentId: string | null
  label?: string
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)

  if (!documentId) return null

  return (
    <>
      <button
        type="button"
        className="btn btn--ghost btn--small"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        {label ?? t('doc_view')}
      </button>
      {open && (
        <DocumentViewer documentId={documentId} onClose={() => setOpen(false)} />
      )}
    </>
  )
}
